// 공개 폼 · 고객관리 ✎ 배지 · 서식 렌더러가 공유하는 필드 SSOT.
// 근거: docs/projects/phone-board/tasks/165-모바일-공개폼-UX-필드확장.md P2,
//       docs/projects/phone-board/tasks/170-서식-렌더러-다운로드-API.md P5.
// 2026-04-19: 폼링크 고정 필드셋 전환. 템플릿 선택에 따른 노출 필드 변동 폐지
//             → FIELD_SECTIONS 로 6 섹션 · 25 필드 고정.
// 2026-05-24 (task 255): 모바일 4탭 입력 + 신규 필드 7종 + 법대/동의는 가입/구매 끝에 합침.
//             4 섹션 · 32 필드 (9 + 7 + 12 + 5 + 신분증/카드 파일 포함). 박성우님 결정사항 박제.
// 2026-05-28: 자동이체 섹션에 카드번호(cardNumber) 추가 → 자동이체 8필드.
// 2026-05-29: 카드 유효기간(cardExpiry) 추가 → 자동이체 9필드. 기기용량(deviceCapacity)은
//             모델명에 용량이 포함되므로 공개폼 노출에서 제외(정의는 레거시 호환 위해 유지).
// 2026-06-06: 재고수령자명(shippingRecipientName) + 손글씨 2칸(nameSign 이름자필 ·
//             noticeSign 안내자필) 추가. 신청자명(applicantName)은 서명 칸들 아래로 이동.
//             손글씨 2칸은 캔버스 자필이며 서식에 이미지로 박힌다(렌더러 다중 서명 지원).
// 2026-06-07: 재고수령자 연락처(shippingRecipientPhone)를 재고수령자명 바로 아래 추가.
//             정규 컬럼 없이 jsonb 저장 — 서식·텍스트변환은 {shippingRecipientPhone}으로 노출.

export type FormFieldInput =
  | "text"
  | "tel"
  | "email"
  | "rrn"
  | "address"
  | "signature"
  | "segmented"
  | "chip"
  | "autocomplete"
  | "file"      // 이미지 업로드 (신분증·카드 사진). 모바일은 카메라 직접 호출.
  | "card"      // 카드번호 — 숫자 입력 시 4자리마다 공백 자동 삽입.
  | "expiry"    // 카드 유효기간 — 숫자 입력 시 MM/YY 형식으로 자동 변환.
  | "number"    // 숫자만 (금액 등).
  // ── 커스텀 항목(field-registry) 전용 — 빌트인 정의에는 쓰지 않는다.
  | "date"        // 날짜 (YYYY-MM-DD)
  | "textarea"    // 여러 줄
  | "select"      // 드롭다운 (options)
  | "multiselect"; // 다중 선택 (options, 값은 쉼표 연결 문자열)

export interface FormFieldEnumOption {
  value: string;
  label: string;
}

export interface FormFieldDef {
  key: string;
  label: string;
  input: FormFieldInput;
  /**
   * 입력칸 안 회색 글씨. 고객이 글자를 치면 사라지므로 "형식 힌트" 용도로만 쓴다.
   * 고객 입력폼에서는 티켓 #863 이후 이 자리를 쓰지 않고 아래 description 으로 옮겨 그린다.
   */
  placeholder?: string;
  /**
   * 항목 이름 바로 아래에 계속 남는 안내 설명 한 줄 (티켓 #863).
   * 값을 입력해도 사라지지 않아, 다 적고 나서 "맞게 썼나"를 확인할 수 있다.
   * 고객이 링크로 여는 입력폼에서만 그린다 — 직원 창은 이 줄을 그리지 않는다.
   */
  description?: string;
  /** segmented / chip 필드용 */
  options?: FormFieldEnumOption[];
  /** "기타" 입력을 허용하는 chip */
  allowOther?: boolean;
  /** 모바일 input autocomplete hint */
  autocomplete?: string;
  /** <input inputmode> 힌트 */
  inputMode?: "numeric" | "tel" | "email" | "text";
  /** input === "autocomplete" 일 때, 어느 카탈로그에서 검색할지. #204
   *  "mvno": 알뜰폰 사업자 — 전국 브랜드 정적 목록(mvno-carriers.ts) 검색 + 직접 입력. */
  catalog?: "models" | "plans" | "mvno";
}

const CARRIER_OPTIONS: FormFieldEnumOption[] = [
  { value: "SKT", label: "SKT" },
  { value: "KT", label: "KT" },
  { value: "LGU+", label: "LGU+" },
];

// 현재 통신사는 신규 가입자를 위해 "없음", 알뜰폰 사용자를 위해 "알뜰폰" 옵션을 추가로 가짐.
// "알뜰폰" 선택 시 어느 알뜰폰 사업자인지 별도 항목(mvnoCarrier)이 펼쳐진다.
const CURRENT_CARRIER_OPTIONS: FormFieldEnumOption[] = [
  ...CARRIER_OPTIONS,
  { value: "MVNO", label: "알뜰폰" },
  { value: "NONE", label: "없음" },
];

/** 현재 통신사 = 알뜰폰 일 때 사업자명 입력(mvnoCarrier)을 노출하기 위한 값. */
export const CURRENT_CARRIER_MVNO_VALUE = "MVNO";

const DELIVERY_METHOD_OPTIONS: FormFieldEnumOption[] = [
  { value: "instant", label: "즉시출고" },
  { value: "onsite", label: "현장수령" },
  { value: "parcel", label: "택배" },
  { value: "quick", label: "퀵" },
];

/** 매장에서 재고를 바로 가져가는 수령방법 — 택배·퀵과 달리 보낼 곳·받는 사람이 필요 없다. */
const DELIVERY_METHODS_WITHOUT_SHIPPING = new Set(["instant", "onsite"]);

/** 수령방법이 즉시출고·현장수령이면 잠그는 재고수령 관련 항목(신청서·공개 입력폼 공통). */
export const SHIPPING_DETAIL_KEYS = new Set([
  "shippingAddress",
  "shippingRecipientName",
  "shippingRecipientPhone",
]);

/** 이 항목을 지금 수령방법 때문에 잠가야 하는가. 결제수단(카드/은행) 잠금과 같은 규칙이다 —
 *  **값은 절대 지우지 않는다**: 택배로 되돌리면 그대로 되살아나야 하고, 재고수령지는 서식
 *  출력에서 주소 폴백을 타므로(crm-decisions D20) 잠금이 값을 건드리면 안 된다.
 *  수령방법 미선택("")이면 잠그지 않는다. */
export function isShippingFieldLocked(
  key: string,
  deliveryMethod?: string,
): boolean {
  return (
    SHIPPING_DETAIL_KEYS.has(key) &&
    DELIVERY_METHODS_WITHOUT_SHIPPING.has(deliveryMethod ?? "")
  );
}

/** 새 결제 정보를 받지 않고 통신사의 현재 이체수단을 유지할 때 저장하는 값. */
export const AUTOPAY_KEEP_EXISTING_VALUE = "keep_existing";

const AUTOPAY_METHOD_OPTIONS: FormFieldEnumOption[] = [
  { value: "card", label: "카드" },
  { value: "bank", label: "은행" },
  { value: AUTOPAY_KEEP_EXISTING_VALUE, label: "기존 이체수단 그대로" },
];

export const FORM_FIELDS: Record<string, FormFieldDef> = {
  // ─── 고객 정보 (7) ─────────────────────────────────
  name: {
    key: "name",
    label: "개통자명",
    input: "text",
    placeholder: "휴대폰 개통할 명의자 성명 (미성년자인경우 자녀이름)",
    autocomplete: "name",
  },
  rrn: {
    key: "rrn",
    label: "주민등록번호",
    input: "rrn",
    placeholder: "기호나 공백없이 숫자만 기재하세요",
  },
  phone: {
    key: "phone",
    label: "개통번호",
    input: "tel",
    placeholder: "숫자만 입력 (- 입력금지)",
    autocomplete: "tel",
    inputMode: "tel",
  },
  address: {
    key: "address",
    label: "주소",
    input: "address",
    placeholder: "실제 거주지 또는 주민등록주소 (통신사 우편물 발송용)",
    autocomplete: "street-address",
  },
  emergencyPhone: {
    key: "emergencyPhone",
    label: "비상연락처",
    input: "tel",
    placeholder: "본인번호외 개통 가능시간에 같이 계실분",
    autocomplete: "tel",
    inputMode: "tel",
  },
  email: {
    key: "email",
    label: "이메일",
    input: "email",
    placeholder: "이메일주소 입력 (대문자 기재필수)",
    autocomplete: "email",
    inputMode: "email",
  },
  shippingAddress: {
    key: "shippingAddress",
    label: "재고수령지",
    input: "address",
    placeholder: "위주소와 동일한경우 비워두세요 (택배는 착불원칙)",
    autocomplete: "street-address",
  },
  // 2026-06-06: 재고를 실제로 받는 사람 이름. 입력폼은 "위 개통자명과 동일"
  // 체크박스로 개통자명을 그대로 채울 수 있다. 재고수령지 바로 아래에 배치.
  shippingRecipientName: {
    key: "shippingRecipientName",
    label: "재고수령자명",
    input: "text",
    placeholder: "재고를 받으실 분 성함 (개통자와 다른 경우)",
    autocomplete: "name",
  },
  // 2026-06-07: 재고수령자명 바로 아래. 실제 재고를 받는 사람 연락처
  // (개통자와 다른 경우 택배·배송 연락용). 재고수령자명과 같은 jsonb 저장 패턴.
  shippingRecipientPhone: {
    key: "shippingRecipientPhone",
    label: "재고수령자 연락처",
    input: "tel",
    placeholder: "재고를 받으실 분 연락처 (개통자와 다른 경우)",
    autocomplete: "tel",
    inputMode: "tel",
  },
  deliveryMethod: {
    key: "deliveryMethod",
    label: "수령방법",
    input: "segmented",
    options: DELIVERY_METHOD_OPTIONS,
  },
  idPhoto: {
    key: "idPhoto",
    label: "신분증 사진",
    input: "file",
    placeholder: "신분증 앞면 (모바일은 카메라로 바로 촬영)",
  },

  // ─── 자동이체 정보 (7) ─────────────────────────────
  autopayMethod: {
    key: "autopayMethod",
    label: "휴대폰 요금 결제 수단",
    input: "segmented",
    options: AUTOPAY_METHOD_OPTIONS,
  },
  // autopayInfo: 은행명·계좌번호·카드사명을 한 칸에 합쳐 저장하던 레거시 필드.
  // 2026-06-07: 카드사명(cardIssuer)·은행명(bankName)·계좌번호(accountNumber)를 각각
  //   독립 필드로 분리. 입력폼·신청서는 분리 필드를 진본으로 쓰되, 합쳐진 한 줄(autopayInfo)도
  //   계속 채워 기존 서식/대리점 템플릿의 {autopayInfo} 토큰과 옛 레코드 호환을 유지한다.
  autopayInfo: {
    key: "autopayInfo",
    label: "자동이체정보 (은행/카드)",
    input: "text",
    placeholder: "은행/카드사명 + 전체번호 (카드는 유효기간 필수)",
  },
  cardIssuer: {
    key: "cardIssuer",
    label: "카드사명",
    input: "text",
    placeholder: "예: 삼성카드, 현대카드",
  },
  cardNumber: {
    key: "cardNumber",
    label: "카드번호",
    input: "card",
    placeholder: "숫자만 입력 (4자리마다 자동 띄어쓰기)",
    inputMode: "numeric",
  },
  cardExpiry: {
    key: "cardExpiry",
    label: "카드 유효기간",
    input: "expiry",
    placeholder: "MMYY 숫자만 입력 (예: 1228 → 12/28)",
    inputMode: "numeric",
  },
  bankName: {
    key: "bankName",
    label: "은행명",
    input: "text",
    placeholder: "예: 국민은행, 신한은행",
  },
  accountNumber: {
    key: "accountNumber",
    label: "계좌번호",
    input: "text",
    placeholder: "숫자만 입력",
    inputMode: "numeric",
  },
  autopayHolder: {
    key: "autopayHolder",
    label: "수납 예금주명",
    input: "text",
    placeholder: "예금주 또는 카드주 성함",
    autocomplete: "name",
  },
  autopayRelation: {
    key: "autopayRelation",
    label: "수납자 관계",
    input: "text",
    placeholder: "본인, 부, 모, 자녀 또는 기타",
  },
  autopayBirth6: {
    key: "autopayBirth6",
    label: "수납명의자 생년월일(6자리)",
    input: "text",
    placeholder: "숫자만 기재 (기호/공백 금지)",
    inputMode: "numeric",
  },
  autopayHolderPhone: {
    key: "autopayHolderPhone",
    label: "수납명의자 연락처",
    input: "tel",
    autocomplete: "tel",
    inputMode: "tel",
  },
  cardPhoto: {
    key: "cardPhoto",
    label: "카드 사진",
    input: "file",
    placeholder: "카드 결제 시 카드 앞면 (CVC 가리고 촬영)",
  },

  // ─── 가입 정보 (8) ─────────────────────────────────
  currentCarrier: {
    key: "currentCarrier",
    label: "현재 통신사",
    input: "segmented",
    options: CURRENT_CARRIER_OPTIONS,
  },
  // 현재 통신사가 "알뜰폰"일 때만 노출되는 사업자명.
  //   공개폼: 전국 브랜드 정적 목록(mvno-carriers.ts) 검색 드롭다운 + 직접 입력 (catalog="mvno").
  //   직원 모달: 매장별 마스터 목록(MasterCombobox kind="mvno"). 모달은 def.key 로 분기하므로
  //   여기 input/catalog 변경의 영향을 받지 않는다.
  mvnoCarrier: {
    key: "mvnoCarrier",
    label: "알뜰폰 사업자",
    input: "autocomplete",
    catalog: "mvno",
    placeholder: "예: 헬로모바일, KT M모바일",
  },
  desiredCarrier: {
    key: "desiredCarrier",
    label: "개통 통신사",
    input: "segmented",
    options: CARRIER_OPTIONS,
  },
  joinType: {
    key: "joinType",
    label: "가입유형",
    input: "segmented",
    options: [
      { value: "number_port", label: "번호이동" },
      { value: "device_change", label: "기기변경" },
      { value: "new", label: "신규가입" },
    ],
  },
  desiredModel: {
    key: "desiredModel",
    label: "모델명",
    input: "autocomplete",
    catalog: "models",
    placeholder: "예: 갤럭시 S25",
  },
  deviceCapacity: {
    key: "deviceCapacity",
    label: "기기용량",
    input: "text",
    placeholder: "예: 256GB",
  },
  desiredColor2: {
    key: "desiredColor2",
    label: "색상 2순위",
    input: "text",
    placeholder: "예: 티타늄 그레이 (선택)",
  },
  desiredColor3: {
    key: "desiredColor3",
    label: "색상 3순위",
    input: "text",
    placeholder: "예: 티타늄 화이트 (선택)",
  },

  // ─── 구매 정보 (3) ─────────────────────────────────
  reportedPrice: {
    key: "reportedPrice",
    label: "보고온 가격",
    input: "text",
    placeholder: "시세표나 상담에서 확인한 금액 (원단위)",
    inputMode: "numeric",
  },
  paymentMethod: {
    key: "paymentMethod",
    label: "현금/할부/페이백",
    input: "segmented",
    options: [
      { value: "cash", label: "현금" },
      { value: "installment", label: "할부" },
      // 2026-06-04: 수납방법에 페이백 추가 (Sentry 7521048362·7521011329).
      { value: "payback", label: "페이백" },
    ],
  },
  cashAmount: {
    key: "cashAmount",
    label: "현금수납 또는 이체금액",
    input: "number",
    placeholder: "원단위 숫자만",
    inputMode: "numeric",
  },

  // ─── 법정대리인 정보 (4) ───────────────────────────
  guardianName: {
    key: "guardianName",
    label: "법정대리인명",
    input: "text",
    autocomplete: "name",
  },
  guardianRelation: {
    key: "guardianRelation",
    label: "법정대리인 관계",
    input: "text",
    placeholder: "부 / 모 / 기타",
  },
  guardianRrn: {
    key: "guardianRrn",
    label: "법정대리인 주민등록번호",
    input: "rrn",
    placeholder: "기호나 공백없이 숫자만 기재",
  },
  guardianPhone: {
    key: "guardianPhone",
    label: "법정대리인 연락처",
    input: "tel",
    autocomplete: "tel",
    inputMode: "tel",
  },

  // ─── 동의 및 서명 (4) ──────────────────────────────
  signature: {
    key: "signature",
    label: "서명/싸인 (이름 정자로 적으시거나 서명/싸인 해주세요)",
    input: "signature",
  },
  // 2026-06-06: 손글씨 두 칸 추가. 둘 다 캔버스 자필이며 서식에 이미지로 박힌다.
  // 키에 "sign" 이 들어가야 렌더러(render.py is_signature_key)가 서명 슬롯으로 인식한다.
  nameSign: {
    key: "nameSign",
    label: "이름 자필 (정자)",
    input: "signature",
    placeholder: "본인 이름을 또박또박 정자로 직접 써주세요",
  },
  noticeSign: {
    key: "noticeSign",
    label: "안내 확인 자필",
    input: "signature",
    placeholder: "\"안내받았음\" 이라고 직접 써주세요",
  },
  // 신청인 성명 — 서명 칸들 아래에 배치(값 입력). 개통자명과 다르면 신청자 이름을 적도록 안내.
  // 2026-07-29(티켓 #105): 안내문을 placeholder(입력 시작하면 사라짐)에서 라벨 괄호로 옮겼다.
  applicantName: {
    key: "applicantName",
    label: "신청자명 (개통자명과 신청자가 다른 경우 신청자 이름을 적어주세요)",
    input: "text",
    autocomplete: "name",
  },

  // ─── 구 상담 9 필드 (#165) — 2026-04-19 고정 필드셋 전환 후 공개폼에서 제외.
  // FORM_FIELDS 에는 남겨둠: ① seed-crm-demo placed_fields.source="submitted.currentPlan"
  // 등 기존 레코드 렌더 유지 ② CrmCustomerDetailPage CONSULTATION_KEYS 의 과거
  // submittedData 표시 호환. 신규 공개폼에서는 수집 안 됨.
  desiredColor: {
    key: "desiredColor",
    label: "색상",
    input: "text",
    placeholder: "예: 티타늄 블랙",
  },
  currentPlan: {
    key: "currentPlan",
    label: "현재 요금제",
    input: "autocomplete",
    catalog: "plans",
    placeholder: "예: 5G 프리미어 에센셜",
  },
  penaltyStatus: {
    key: "penaltyStatus",
    label: "위약금 유무",
    input: "segmented",
    options: [
      { value: "penalty_yes", label: "있음" },
      { value: "penalty_no", label: "없음" },
      { value: "penalty_unknown", label: "확인중" },
    ],
  },
  bundleType: {
    key: "bundleType",
    label: "결합 유무",
    input: "segmented",
    options: [
      { value: "bundle_none", label: "없음" },
      { value: "bundle_family", label: "가족결합" },
      { value: "bundle_internet_tv", label: "인터넷·TV" },
      { value: "bundle_other", label: "기타" },
    ],
  },
  subsidyStatus: {
    key: "subsidyStatus",
    label: "보조금 유무",
    input: "segmented",
    options: [
      { value: "subsidy_yes", label: "있음" },
      { value: "subsidy_no", label: "없음" },
    ],
  },
  acquisitionChannel: {
    key: "acquisitionChannel",
    label: "유입 경로",
    input: "chip",
    allowOther: true,
    options: [
      { value: "kakao", label: "카카오채널" },
      { value: "daangn", label: "당근" },
      { value: "naver", label: "네이버" },
      { value: "referral", label: "지인" },
    ],
  },

  // 개통(희망) 요금제. 공개폼 "가입 정보" 섹션 + 서식 placed_fields.fieldKey 양쪽에서 사용.
  // catalog: "plans" → 제로노트 적재 요금제 목록에서 검색 선택(통신사별). 고객 선택값이
  // 서식 자리표시자와 정산 화면 공시지원금 조회(월정액 가격 매칭)에 그대로 재사용된다 (task 266).
  desiredPlan: {
    key: "desiredPlan",
    label: "희망 요금제",
    input: "autocomplete",
    catalog: "plans",
    placeholder: "예: 5G 프라임",
  },
  birth: {
    key: "birth",
    label: "생년월일",
    input: "text",
    placeholder: "YYYY-MM-DD",
  },
  applyDate: {
    key: "applyDate",
    label: "신청일",
    input: "text",
    placeholder: "YYYY-MM-DD",
  },
  applicantSign: {
    key: "applicantSign",
    label: "신청인 서명",
    input: "signature",
  },
};

export type FormFieldKey = keyof typeof FORM_FIELDS;

export const FORM_FIELD_KEYS = Object.keys(FORM_FIELDS) as FormFieldKey[];

/** 공개 폼 섹션 · 고정 25 필드. PublicFormPage 렌더 순서 SSOT. */
export interface FormFieldSection {
  title: string;
  keys: FormFieldKey[];
}

// 2026-05-24 (task 255), 2026-08-01 (tickets 190, 192): 모바일 4탭.
// 고객정보 상단은 개통번호 → 법정대리인 4필드 → 이름 자필 → 서명 → 주소 순으로 유지한다.
export const FIELD_SECTIONS: FormFieldSection[] = [
  {
    title: "고객 정보",
    keys: [
      "name",
      "rrn",
      "phone",
      // 법정대리인 (미성년 개통 시에만 노출·필수). 개통번호 다음, 주소 앞에서 먼저 작성한다.
      "guardianName",
      "guardianRelation",
      "guardianRrn",
      "guardianPhone",
      "nameSign",
      "signature",
      "address",
      "emergencyPhone",
      "email",
      "shippingAddress",
      "shippingRecipientName",
      "shippingRecipientPhone",
      "deliveryMethod",
      "idPhoto",
    ],
  },
  {
    title: "가입 정보",
    keys: [
      "currentCarrier",
      // 현재 통신사 = "알뜰폰" 선택 시에만 노출 (isVisible 가 게이트). 사업자명 검색 드롭다운.
      "mvnoCarrier",
      "desiredCarrier",
      "joinType",
      "desiredModel",
      // desiredPlan(희망 요금제): 고객 입력 항목에서 은퇴 (2026-06-06). 공개폼·기타 섹션 노출 제거.
      //   정의는 FORM_FIELDS 에 남겨 기존 data·서식 자리표시자의 라벨 해석과 공시지원금
      //   자동채움(RetailerInfoEditModal: retailerValues.plan 폴백)이 계속 동작하게 한다.
      // deviceCapacity: 모델명에 용량이 포함돼 별도 입력 불필요 → 공개폼 노출 제외 (2026-05-29).
      "desiredColor",
      "desiredColor2",
      "desiredColor3",
    ],
  },
  {
    title: "자동이체 정보",
    keys: [
      "autopayMethod",
      // autopayInfo(합쳐진 한 줄)는 입력폼에서 AutopayInfoRow 렌더 트리거로만 쓰이고,
      // 실제 입력은 아래 분리 필드(카드사·카드번호·유효기간 / 은행명·계좌번호)로 받는다.
      "autopayInfo",
      "cardIssuer",
      "cardNumber",
      "cardExpiry",
      "bankName",
      "accountNumber",
      "autopayHolder",
      "autopayRelation",
      "autopayBirth6",
      "autopayHolderPhone",
      "cardPhoto",
    ],
  },
  {
    title: "구매 정보",
    keys: [
      "reportedPrice",
      "paymentMethod",
      "cashAmount",
      // 2026-08-01: 이름 자필·서명은 고객정보 상단으로 이동. 안내 확인 자필만 구매정보에 남기고,
      // 그 아래 신청자명을 입력한다.
      "noticeSign",
      "applicantName",
    ],
  },
];

/** 공개 폼 렌더 순서 = 섹션 평탄화. 고정 필드셋이라 FAB 선택 여부와 무관. */
export const PUBLIC_FORM_FIELD_ORDER: FormFieldKey[] = FIELD_SECTIONS.flatMap(
  (s) => s.keys,
);

/** FAB 발급 시 exposedFields 기본값 = 전체 고정 필드셋. */
export const FAB_DEFAULT_EXPOSED: FormFieldKey[] = PUBLIC_FORM_FIELD_ORDER;

/** FAB 기본 required = 이미지 * 표기 필드 (개통자명·주민번호·개통번호·주소 + 신청인성명·서명). */
export const FAB_DEFAULT_REQUIRED: FormFieldKey[] = [
  "name",
  "rrn",
  "phone",
  "address",
  "applicantName",
  "signature",
];

/** 동의 문구 — 고객 정보 탭의 첫 서명 칸(이름 자필 또는 서명) 바로 위에 노출. */
export const AGREEMENT_TEXT = [
  "위내용을 모두 확인 하였으며 이해하고 동의합니다.",
  "이 서류에 서명하는 것으로 매장에 신청한 업무에 대해 위임 효력을 가지고 필요한 경우 해당 업무에 관련한 서명, 대필을 허락하며 위임장의 효력을 갖습니다.",
  "서명 대필 위임 후 작성 내용과 다른 문제(추가회선무단개통등) 가 생기는 경우 민형사상 책임은 \u2018판매점\u2019 에서 지고 작성내용과 일치하는 내용에 대한 이의제기시 그 내용에 대한 책임은 \u2018신청자\u2019가 집니다.",
];

/** enum value → label 헬퍼. 고객관리 탭·렌더러가 한국어 라벨을 찍을 때 사용. */
export function labelForEnum(fieldKey: string, value: unknown): string {
  const def = FORM_FIELDS[fieldKey];
  if (!def?.options || typeof value !== "string") return String(value ?? "");
  return def.options.find((o) => o.value === value)?.label ?? value;
}

/**
 * 서식 필드 키 → 고객 입력폼(customer-form-config) 키 별칭.
 *
 * 서식 채우기는 FORM_FIELDS(25 고정셋) 키를, 고객 입력폼은
 * customer-form-config 의 builtin 키를 쓴다. 같은 의미인데 이름이 달라
 * 자동 채움이 끊기던 항목들을 이어주는 단방향 폴백 맵.
 *
 * 서식 source 해소(server render.ts · client FormFillPage)에서, 원래 키로
 * 값을 못 찾으면 여기 나열된 후보 키를 순서대로 더 찾는다.
 *
 *   currentCarrier(서식 "현재 통신사") ← prevCarrier(입력폼 "이전 통신사")
 *   desiredCarrier(서식 "개통 통신사") ← carrier(입력폼 "통신사")
 */
export const FORM_FIELD_KEY_ALIASES: Record<string, string[]> = {
  currentCarrier: ["prevCarrier"],
  desiredCarrier: ["carrier"],
};

/** 원래 키 + 별칭 후보를 순서대로 나열한다. 값 해소 시 이 순서로 탐색. */
export function fieldKeyCandidates(key: string): string[] {
  return [key, ...(FORM_FIELD_KEY_ALIASES[key] ?? [])];
}

/**
 * 레거시 호환: 분리 필드(bankName/accountNumber/cardIssuer)가 생기기 전(~2026-06-07)
 * 레코드는 은행명·계좌번호·카드사명을 autopayInfo 한 칸에 합쳐 저장했다.
 *   - 은행 결제: "은행명 계좌번호" (첫 공백 기준 분리)
 *   - 카드 결제: "카드사명" (공백 없는 한 단어)
 * 분리 필드가 비어 있을 때만 이 합쳐진 값에서 해당 조각을 뽑아 보여주기 위한 단방향 폴백.
 * 신청서에서 한 번 저장하면 분리 필드로 확정돼 다음부터는 이 폴백을 타지 않는다.
 */
export function legacyAutopayPart(
  key: "bankName" | "accountNumber" | "cardIssuer",
  autopayInfo: unknown,
  autopayMethod?: unknown,
): string {
  const s = typeof autopayInfo === "string" ? autopayInfo.trim() : "";
  if (!s) return "";
  const idx = s.indexOf(" ");
  // 공백 없는 한 단어 + 카드 결제 → 카드사명으로 본다.
  const cardOnly = idx < 0 && autopayMethod === "card";
  if (key === "cardIssuer") return cardOnly ? s : "";
  if (cardOnly) return ""; // 카드 전용 레거시엔 은행 정보가 없다.
  if (idx < 0) return key === "bankName" ? s : ""; // 공백 없으면 은행명만 있는 것으로
  return key === "bankName" ? s.slice(0, idx) : s.slice(idx + 1).trim();
}
